import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { salesAPI } from '../backend';
import { formatPhone } from '../utils/formatPhone';

interface SaleProduct {
  sale_id: number;
  product_id: number;
  quantity: number;
  price: number;
  cost: number;
  quantity_paid?: number;
  product?: {
    id: number;
    name: string;
    image: string;
  };
}

interface Sale {
  id: number;
  client_id: number;
  delivery_method: string;
  payment_status: string;
  total: number;
  created_at: string;
  client?: {
    id: number;
    name: string;
    email: string;
    phone: string;
  };
  sale_products: SaleProduct[];
}

interface PaymentDialogProps {
  sale: Sale;
  isOpen: boolean;
  onClose: () => void;
  onPaymentComplete: () => void;
}

function PaymentDialog({ sale, isOpen, onClose, onPaymentComplete }: PaymentDialogProps) {
  const [payments, setPayments] = useState<{ [productId: number]: number }>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && sale) {
      // Initialize payments with current paid quantities
      const initialPayments: { [productId: number]: number } = {};
      sale.sale_products.forEach(sp => {
        initialPayments[sp.product_id] = sp.quantity_paid || 0;
      });
      setPayments(initialPayments);
    }
  }, [isOpen, sale]);

  const handleQuantityChange = (productId: number, newQuantity: number) => {
    if (!sale) return;
    
    const product = sale.sale_products.find(sp => sp.product_id === productId);
    if (!product) return;

    // Ensure quantity doesn't exceed ordered quantity
    const maxQuantity = product.quantity;
    const clampedQuantity = Math.max(0, Math.min(newQuantity, maxQuantity));
    
    setPayments(prev => ({
      ...prev,
      [productId]: clampedQuantity
    }));
  };

  const calculateProductProgress = (product: SaleProduct) => {
    const paidQuantity = payments[product.product_id] || 0;
    const percentage = (paidQuantity / product.quantity) * 100;
    const amountPaid = paidQuantity * product.price;
    const totalAmount = product.quantity * product.price;
    
    return {
      paidQuantity,
      percentage,
      amountPaid,
      totalAmount,
      remaining: product.quantity - paidQuantity,
      remainingAmount: totalAmount - amountPaid
    };
  };

  const calculateSaleProgress = () => {
    if (!sale || !sale.sale_products) {
      return {
        totalPaid: 0,
        totalAmount: 0,
        paidProducts: 0,
        totalProducts: 0,
        percentage: 0,
        isFullyPaid: false
      };
    }

    let totalPaid = 0;
    let totalAmount = 0;
    let paidProducts = 0;
    let totalProducts = sale.sale_products.length;

    sale.sale_products.forEach(product => {
      const progress = calculateProductProgress(product);
      totalPaid += progress.amountPaid;
      totalAmount += progress.totalAmount;
      if (progress.paidQuantity >= product.quantity) {
        paidProducts++;
      }
    });

    return {
      totalPaid,
      totalAmount,
      paidProducts,
      totalProducts,
      percentage: totalAmount > 0 ? (totalPaid / totalAmount) * 100 : 0,
      isFullyPaid: paidProducts === totalProducts
    };
  };

  const handleSavePayments = async () => {
    if (!sale) return;
    
    // Check if any quantities are being decreased from original values
    const decreasedProducts = sale.sale_products.filter(product => {
      const newQuantity = payments[product.product_id] || 0;
      const originalQuantity = product.quantity_paid || 0;
      return newQuantity < originalQuantity;
    });

    if (decreasedProducts.length > 0) {
      const productNames = decreasedProducts.map(p => p.product?.name).join(', ');
      const confirmed = window.confirm(
        `¿Estás seguro de que quieres reducir las cantidades pagadas para: ${productNames}? Esta acción puede afectar el historial de pagos.`
      );
      if (!confirmed) {
        return; // User cancelled the action
      }
    }
    
    setLoading(true);
    try {
      // Update each product's quantity_paid
      const updatePromises = sale.sale_products.map(product => {
        const paidQuantity = payments[product.product_id] || 0;
        return salesAPI.updateProductPayment(sale.id, product.product_id, paidQuantity);
      });

      await Promise.all(updatePromises);

      // Check if all products are fully paid and update sale status if needed
      const saleProgress = calculateSaleProgress();
      if (saleProgress.isFullyPaid && sale.payment_status !== 'Pagado') {
        await salesAPI.update(sale.id, { payment_status: 'Pagado' });
      }

      onPaymentComplete();
    } catch (error) {
      console.error('Error saving payments:', error);
      alert('Error al guardar los pagos. Por favor intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const saleProgress = calculateSaleProgress();

  if (!isOpen || !sale || (sale as any).sale_type === 'Muestra') return null;

  return (
    <div className="dialog-overlay">
      <div className="dialog payment-dialog">
        <div className="dialog-header">
          <h2>Registrar Abono - Venta #{sale.id}</h2>
          <button className="dialog-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="dialog-content">
          {/* Customer Info */}
          <div className="sale-customer">
            <h3 className="client-item name">{sale.client?.name}</h3>
            <p className="client-item">{sale.client?.email}</p>
            <p className="client-item">{formatPhone(sale.client?.phone)}</p>
          </div>

          {/* Overall Progress */}
          <div className="overall-progress">
            <div className="progress-header">
              <h4>Progreso General</h4>
              <span className="progress-percentage">
                {saleProgress.percentage.toFixed(1)}%
              </span>
            </div>
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ width: `${saleProgress.percentage}%` }}
              ></div>
            </div>
            <div className="progress-summary">
              <span>${saleProgress.totalPaid.toFixed(2)} de ${saleProgress.totalAmount.toFixed(2)}</span>
              <span>{saleProgress.paidProducts} de {saleProgress.totalProducts} productos pagados</span>
            </div>
          </div>

          {/* Products List */}
          <div className="products-payment-list">
            <h4>Productos</h4>
            {sale.sale_products.map((product, index) => {
              const progress = calculateProductProgress(product);
              return (
                <div key={index} className="product-payment-item">
                  <div className="product-info">
                    <div className="product-details">
                      <h5>{product.product?.name}</h5>
                      <p className="product-meta">
                        Cantidad: {product.quantity} | Precio: ${product.price}
                      </p>
                    </div>
                    <div className="product-progress">
                      <div className="progress-bar small">
                        <div 
                          className="progress-fill" 
                          style={{ width: `${progress.percentage}%` }}
                        ></div>
                      </div>
                      <span className="progress-text">
                        {progress.paidQuantity}/{product.quantity} pagados
                      </span>
                    </div>
                  </div>
                  
                  <div className="payment-controls">
                    <div className="quantity-controls">
                      <button
                        className="quantity-btn"
                        onClick={() => handleQuantityChange(product.product_id, progress.paidQuantity - 1)}
                        disabled={progress.paidQuantity <= 0}
                      >
                        -
                      </button>
                      <span className="quantity-display">
                        {progress.paidQuantity}
                      </span>
                      <button
                        className="quantity-btn"
                        onClick={() => handleQuantityChange(product.product_id, progress.paidQuantity + 1)}
                        disabled={progress.paidQuantity >= product.quantity}
                      >
                        +
                      </button>
                    </div>
                    <div className="amount-info">
                      <span className="amount-paid">
                        ${progress.amountPaid.toFixed(2)}
                      </span>
                      {progress.remaining > 0 && (
                        <span className="amount-remaining">
                          Faltan: ${progress.remainingAmount.toFixed(2)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="dialog-footer">
          <button className="btn-secondary" onClick={onClose}>
            Cancelar
          </button>
          <button 
            className="btn-primary" 
            onClick={handleSavePayments}
            disabled={loading}
          >
            {loading ? 'Guardando...' : 'Guardar Abono'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default PaymentDialog; 